<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div class="device-icon-pin" :class="sizeClass" :title="device.deviceName">
      <ControlStatusRing :value="ringValue" :accent="accent">
        <div class="heat-pump-pin-art">
          <HeatPumpAnimation :kind="heatPumpKind" :active="running" :mode="tone" />
        </div>
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="running"
      :accent="accent"
      class="heat-pump-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <img v-if="thumbUrl" :src="thumbUrl" alt="" class="heat-pump-card-face__img" draggable="false" />
        <HeatPumpAnimation
          v-else
          class="heat-pump-card-face__figure"
          :kind="heatPumpKind"
          :active="running"
          :mode="tone" />
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="heat-pump-card-face__value">{{ headline }}</span>
      </template>

      <template v-if="cardFacts.length" #facts>
        <span v-for="fact in cardFacts" :key="fact" class="heat-pump-card-face__fact">{{ fact }}</span>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'hvac'"
      :name="device.deviceName"
      :is-on="running"
      :active-color="tone === 'cool' ? 'sky-400' : 'orange-400'"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <div class="heat-pump-sheet" :style="{ '--sheet-accent': accent }">
      <!-- HERO — the machine itself, running when its compressor runs. -->
      <div class="heat-pump-hero">
        <div class="heat-pump-hero-figure">
          <HeatPumpAnimation :kind="heatPumpKind" :active="running" :mode="tone" />
        </div>
        <div class="heat-pump-hero-value">{{ currentText ?? '—' }}</div>
        <div class="heat-pump-hero-caption">{{ running ? modeLabel : offLabel }} · {{ kindLabel }}</div>
      </div>

      <!-- WHAT IT REPORTS — flow and return, the outside, the compressor. -->
      <div v-if="sheetRows.length" class="heat-pump-rows">
        <div v-for="row in sheetRows" :key="row.key" class="heat-pump-row">
          <span class="heat-pump-row-key">{{ row.label }}</span>
          <span class="heat-pump-row-value">{{ row.value }}</span>
        </div>
      </div>

      <!-- ITS OWN CONTROLS — power, the mode, and the target the loop works to. -->
      <ControlToggleRow
        v-if="hasPower"
        :model-value="powerOn"
        :label="tt('Power', 'Vklop')"
        icon="power_settings_new"
        :disabled="controlling"
        @update:model-value="setPower" />

      <div v-if="modeOptions.length" class="heat-pump-modes">
        <ControlChip
          v-for="option in modeOptions"
          :key="option.value"
          :active="modeCode === option.value"
          small
          @click="setMode(option.value)">
          {{ option.label }}
        </ControlChip>
      </div>

      <div v-if="target !== null" class="heat-pump-setpoint">
        <span class="heat-pump-setpoint-label">{{ tt('Target', 'Cilj') }}</span>
        <ControlStepper :value-text="targetText ?? '—'" hold :disabled="controlling" @step="stepTarget" />
      </div>

      <!-- HISTORY — the temperatures are logged; keep their log door. -->
      <div v-if="loggableValues.length" class="heat-pump-history">
        <p class="heat-pump-history-title">
          <ControlIcon name="activity" size="1em" />
          {{ tt('History', 'Zgodovina') }}
        </p>
        <div class="heat-pump-history-rows">
          <ValueLogButton
            v-for="v in loggableValues"
            :key="v.id"
            :device-value="v"
            :device="{ id: device.id, deviceName: device.deviceName }" />
        </div>
      </div>

      <p v-if="lastUpdated" class="heat-pump-updated">
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { heatPumpKindOf, type HeatPumpKind } from './deviceWidgetTypes';
import { HEAT_PUMP_VARIANTS } from '../device-editor/variants/heatPump';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlStepper from '../device-sheet/ControlStepper.vue';
import HeatPumpAnimation from './heat-pump/HeatPumpAnimation.vue';
import ValueLogButton from './ValueLogButton.vue';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (values) => {
    if (values?.length) initializeValues(values);
  },
  { deep: true },
);

/** The live reading, then the device row — the same fold the other tiles use. */
function rowValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return (props.device.deviceValues ?? []).find((value) => value.valueKey === key)?.currentValue;
}

function numeric(keys: string[]): number | null {
  for (const key of keys) {
    const raw = rowValue(key);
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function truthy(keys: string[]): boolean {
  return keys.some((key) => {
    const raw = rowValue(key);
    return raw === true || raw === 1 || raw === 'on' || raw === 'true';
  });
}

function hasReading(key: string): boolean {
  return (props.device.deviceValues ?? []).some((value) => value.valueKey === key);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

// ── The machine ──────────────────────────────────────────────────────────
/** Where it takes its heat from — the editor's pick, then the icon and the name. */
const heatPumpKind = computed<HeatPumpKind>(() =>
  heatPumpKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);

const kindLabel = computed(() => {
  const meta = HEAT_PUMP_VARIANTS.options.find((entry) => entry.value === heatPumpKind.value);
  return meta ? tt(meta.label[0], meta.label[1]) : '';
});

const hasPower = computed(() => hasReading('on_off'));
const powerOn = computed(() => truthy(['on_off']));

const current = computed(() => numeric(['current_temperature']));
const target = computed(() => numeric(['target_temperature']));
const flow = computed(() => numeric(['flow_temperature']));
const returnTemp = computed(() => numeric(['return_temperature']));
const outside = computed(() => numeric(['outside_temperature']));
const power = computed(() => numeric(['active_power']));
const compressor = computed(() => truthy(['compressor_state']));

/** The operation mode's raw code, whatever key the integration spells it with. */
const modeCode = computed<number | null>(() => numeric(['operation_mode']));

const MODES = [
  { value: 0, en: 'Off', sl: 'Izklop' },
  { value: 1, en: 'Auto', sl: 'Samodejno' },
  { value: 2, en: 'Heat', sl: 'Gretje' },
  { value: 3, en: 'Cool', sl: 'Hlajenje' },
  { value: 4, en: 'Eco', sl: 'Eko' },
  { value: 5, en: 'Hot water', sl: 'Topla voda' },
];

/** The modes the device actually publishes — a class without the enum shows none. */
const modeOptions = computed(() =>
  hasReading('operation_mode') ? MODES.map((entry) => ({ value: entry.value, label: tt(entry.en, entry.sl) })) : [],
);

const modeLabel = computed(() => {
  const entry = MODES.find((candidate) => candidate.value === modeCode.value);
  return entry ? tt(entry.en, entry.sl) : tt('Running', 'Deluje');
});

const offLabel = computed(() => tt('Standby', 'V pripravljenosti'));

/** Running means the compressor turns — a heat pump switched on but idle is not. */
const running = computed(() => (hasReading('compressor_state') ? compressor.value : powerOn.value));

/** Warm while it heats (and for hot water), cold while it cools, neutral otherwise. */
const tone = computed<'heat' | 'cool' | 'idle'>(() => {
  if (!running.value) return 'idle';
  if (modeCode.value === 3) return 'cool';
  if (modeCode.value === 2 || modeCode.value === 5) return 'heat';
  return 'idle';
});

const currentText = computed(() => (current.value === null ? null : `${formatNumber(current.value)} °C`));
const targetText = computed(() => (target.value === null ? null : `${formatNumber(target.value)} °C`));
const flowText = computed(() => (flow.value === null ? null : `${formatNumber(flow.value)} °C`));
const returnText = computed(() => (returnTemp.value === null ? null : `${formatNumber(returnTemp.value)} °C`));
const outsideText = computed(() => (outside.value === null ? null : `${formatNumber(outside.value)} °C`));
const powerText = computed(() => (power.value === null ? null : `${formatNumber(power.value)} W`));

const headline = computed(() => currentText.value ?? modeLabel.value);

const cardFacts = computed(() =>
  [
    running.value ? modeLabel.value : null,
    flowText.value ? `${tt('Flow', 'Dvižni vod')} ${flowText.value}` : null,
  ].filter((fact): fact is string => fact !== null),
);

const sheetRows = computed(() => {
  const rows: Array<{ key: string; label: string; value: string }> = [];
  if (targetText.value) rows.push({ key: 'target', label: tt('Target', 'Cilj'), value: targetText.value });
  if (flowText.value) rows.push({ key: 'flow', label: tt('Flow', 'Dvižni vod'), value: flowText.value });
  if (returnText.value) rows.push({ key: 'return', label: tt('Return', 'Povratni vod'), value: returnText.value });
  if (outsideText.value) rows.push({ key: 'outside', label: tt('Outside', 'Zunanja'), value: outsideText.value });
  if (powerText.value) rows.push({ key: 'power', label: tt('Power draw', 'Moč'), value: powerText.value });
  if (hasReading('compressor_state')) {
    rows.push({
      key: 'compressor',
      label: tt('Compressor', 'Kompresor'),
      value: compressor.value ? tt('Running', 'Deluje') : tt('Idle', 'Miruje'),
    });
  }
  return rows;
});

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

const loggableValues = computed(() =>
  (props.device.deviceValues ?? []).filter((value) => value.loggingType && value.loggingType !== 'None'),
);

const thumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** Heating's own colour, cooling's own — the sheet, the ring and the card share it. */
const accent = computed(() => (tone.value === 'cool' ? 'rgb(56, 189, 248)' : 'rgb(249, 115, 22)'));

/** The ring around the pin: how far the room is from the target, or the power. */
const ringValue = computed(() => {
  if (current.value === null || target.value === null) return null;
  const gap = Math.abs(target.value - current.value);
  return Math.max(0, Math.min(100, Math.round((1 - gap / 6) * 100)));
});

// ── Actions ──────────────────────────────────────────────────────────────
async function setPower(next: boolean) {
  await setProperty(props.device, 'on_off', next);
}

async function setMode(code: number) {
  await setProperty(props.device, 'operation_mode', code);
}

async function stepTarget(delta: 1 | -1) {
  if (target.value === null) return;
  const next = Math.round((target.value + delta * 0.5) * 2) / 2;
  await setProperty(props.device, 'target_temperature', next);
}
</script>

<style scoped>
/* The machine inside the pin: sized off the ring's clear circle, like the fan's
   own drawn icon, so every pin size scales with it. */
.heat-pump-pin-art {
  width: 66%;
  height: 66%;
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
.heat-pump-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.heat-pump-card-face__figure {
  width: 100%;
  height: 100%;
}

.heat-pump-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  color: #c2410c;
}

.dark .heat-pump-card-face__value {
  color: #fdba74;
}

.heat-pump-card-face__fact + .heat-pump-card-face__fact::before {
  content: '·';
  margin: 0 0.3em;
  color: rgb(148, 163, 184);
}

.heat-pump-card-face__fact {
  font-size: var(--card-fact, 0.7rem);
  color: #64748b;
  white-space: nowrap;
}

.dark .heat-pump-card-face__fact {
  color: #94a3b8;
}

/* ── Control mode: the sheet body ─────────────────────────────────────────── */
.heat-pump-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
}

.heat-pump-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(0.375rem * var(--ui-space-factor));
}

.heat-pump-hero-figure {
  width: calc(8.5rem * var(--ui-space-factor));
  height: calc(8.5rem * var(--ui-space-factor));
  color: rgb(var(--gray-600));
}

.dark .heat-pump-hero-figure {
  color: rgb(var(--gray-300));
}

.heat-pump-hero-value {
  font-size: calc(1.6rem * var(--ui-space-factor));
  font-weight: 700;
  color: rgb(var(--gray-800));
}

.dark .heat-pump-hero-value {
  color: rgb(var(--gray-100));
}

.heat-pump-hero-caption {
  font-size: calc(0.8rem * var(--ui-space-factor));
  color: rgb(var(--gray-500));
}

.heat-pump-rows {
  display: flex;
  flex-direction: column;
  border-radius: calc(0.75rem * var(--ui-space-factor));
  background: rgba(148, 163, 184, 0.08);
  padding: calc(0.25rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
}

.heat-pump-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(0.4rem * var(--ui-space-factor)) 0;
  font-size: calc(0.82rem * var(--ui-space-factor));
}

.heat-pump-row + .heat-pump-row {
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.heat-pump-row-key {
  color: rgb(var(--gray-500));
}

.heat-pump-row-value {
  font-weight: 600;
  color: rgb(var(--gray-800));
}

.dark .heat-pump-row-value {
  color: rgb(var(--gray-100));
}

.heat-pump-modes {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.375rem * var(--ui-space-factor));
}

.heat-pump-setpoint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.heat-pump-setpoint-label {
  font-size: calc(0.82rem * var(--ui-space-factor));
  color: rgb(var(--gray-500));
}

.heat-pump-history {
  display: flex;
  flex-direction: column;
  gap: calc(0.5rem * var(--ui-space-factor));
}

.heat-pump-history-title {
  display: flex;
  align-items: center;
  gap: 0.35em;
  font-size: calc(0.78rem * var(--ui-space-factor));
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.heat-pump-history-rows {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.375rem * var(--ui-space-factor));
}

.heat-pump-updated {
  font-size: calc(0.72rem * var(--ui-space-factor));
  color: rgb(var(--gray-400));
}
</style>
